import {
  IAgoraRTCClient,
  NetworkQuality,
  RemoteAudioTrackStats,
  RemoteVideoTrackStats,
} from "agora-rtc-sdk-ng";
import { useAtom } from "jotai";
import { mapKeys } from "lodash";
import { useEffect, useState } from "react";
import { AgoraRoom } from "../../av/AgoraService";
import { roomAtom } from "../../jotai/atoms";
import { useFeatureFlags, useUserInfos } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import { User } from "../../types";
import { extractUserId } from "../../utils/session";
import useInterval from "../../utils/useInterval";

type Stat = {
  [sectionTitle: string]: Metric;
};

type Metric = Record<string, number | string>;

export default function AgoraStat() {
  const [room] = useAtom(roomAtom);
  const [stat, setStat] = useState<Stat>({});
  const [networkStat, setNetworkStat] = useState<NetworkQuality | null>(null);
  const [userInfos] = useUserInfos();
  const [featureFlags] = useFeatureFlags();

  const show = featureFlags?.agoraStat;

  useInterval(() => {
    if (!(room instanceof AgoraRoom) || !show) return;

    const { client } = room;
    const stat = {
      ["local audio"]: client.getLocalAudioStats(),
      ["local video"]: client.getLocalVideoStats(),
      rtc: client.getRTCStats(),
      ...getRemoteUserStat(client, userInfos),
    };

    setStat(stat as unknown as Stat);
  }, 1000);

  useEffect(() => {
    if (!(room instanceof AgoraRoom) || !show) return;

    function onQuality(network: NetworkQuality) {
      setNetworkStat(network);
    }

    room.client.on("network-quality", onQuality);

    return () => {
      room.client.off("network-quality", onQuality);
    };
  });

  if (!show) return null;

  const allStat = { network: networkStat ?? {}, ...stat };

  return (
    <Div>
      {Object.entries(allStat).map(([title, metric]) => {
        return <MetricTable key={title} title={title} metric={metric} />;
      })}
    </Div>
  );
}

function MetricTable({ title, metric }: { title: string; metric: Metric }) {
  return (
    <section>
      <div className="title">{title}</div>
      <Table>
        {Object.entries(metric)
          .sort()
          .map(([k, v]) => {
            k = convertCasing(k);
            return (
              <>
                <div title={k}>{k}</div>
                <div>{v}</div>
              </>
            );
          })}
      </Table>
    </section>
  );
}

/**
 *
 * NOTE: not displaying it as Agora seemed to return empty data
 * on each stats call
 * @param client
 * @param userInfo
 * @returns
 */
function getRemoteUserStat(
  client: IAgoraRTCClient,
  userInfo: Record<User["id"], User>
) {
  const audios = client.getRemoteAudioStats();
  const videos = client.getRemoteVideoStats();
  const networks = client.getRemoteNetworkQuality();

  const sessionIds = [
    ...Object.keys(audios),
    ...Object.keys(videos),
    ...Object.keys(networks),
  ];

  // console.log(audios, videos, networks);
  const usersStat: Stat = {};

  for (const sid of sessionIds) {
    const uid = extractUserId(sid);
    if (!uid) continue;

    const user = userInfo[uid];
    if (!user) continue;

    let metric: Metric = {};

    if (audios[sid]) {
      const audio = prefix(audios[sid], "a_") as unknown as Metric;
      metric = { ...metric, ...audio };
    }
    if (videos[sid]) {
      const video = prefix(videos[sid], "v_") as unknown as Metric;
      metric = { ...metric, ...video };
    }
    if (networks[sid]) {
      metric = { ...metric, ...networks[sid] };
    }

    usersStat[user.name] = metric;
  }

  return usersStat;
}

function prefix(
  metric: RemoteAudioTrackStats | RemoteVideoTrackStats,
  pre: string
) {
  return mapKeys(metric, (v, k) => `${pre}${k}`);
}

/**
 * convert camel casing to spaced separated string
 * "sendBytes" -> "send bytes"
 */
function convertCasing(key: string) {
  if (key === "RTT") return key;

  return key
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase();
}

const Table = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(1, 100px 40px)",
  columnGap: 2,

  div: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
});

const Div = styled("div", {
  position: "fixed",
  top: 80,
  right: 0,
  zIndex: "$meter",
  background: "black",
  color: "white",
  fontSize: 10,
  padding: "0.5em",
  userSelect: "text",
  maxHeight: 500,
  overflowY: "scroll",

  ".title": {
    fontWeight: "bold",
    borderBottom: "0.5px solid white",
    marginBottom: 2,
  },

  section: {
    marginBottom: "0.5em",
  },
});
